import { gsap } from 'gsap'
import { FormatService } from 'services/FormatService'
import { signal } from 'services/SignalsService'

export default class BitmapBaseValue extends PIXI.Container {
  constructor(data) {
    super()

    this.className = 'BitmapBaseValue'

    this.init(data)

    const baseValue = this.createBaseValue()
    this.addChild(baseValue)

    this.createBitmap(baseValue, data)

    if (this.hasCurrency) {
      this.createCurrency(data)
    }

    this.baseValue = baseValue

    this.createDebug(baseValue, data)

    if (data.bigwin) {
      this.replaceBigWinCounter()
    }

    this.center(data)
  }

  center(){
    this.baseValue.x =  this.debug.width / 2
    this.baseValue.y =  this.debug.height / 2

    this.bitmapText.x = 0
    this.bitmapText.y = 0
    this.bitmapText.text = 0
    this.bitmapText.fontSize = this._data.fontSize || 350
    this.currencyImage.x = this.bitmapText._textWidth * this._currencyProps.offsetX
    this.currencyImage.y = this._currencyProps.offsetY
  }

  replaceBigWinCounter() {
    game.bigwin.counter = this
  }

  createDebug(baseValue, data) {
    const debug = new PIXI.Graphics()
    debug.lineStyle(1, 0xFF0000, 0.8)
    debug.drawRect(data.x, data.y, data.width, data.height)
    debug.endFill()
    this.addChild(debug)

    this.debug = debug
    debug.visible = false
  }

  init(data) {
    this.addMainProperty(data)
    this.createSignals(data)
  }

  addMainProperty(data) {
    this._data = data
    this._data.style.fontName = data.style.font
    this.visible = data.visible || false
    this.hasCurrency = data.hasCurrency || true
    this.maxWidth = data.maxWidth || 0
  }

  reset() {
    this.bitmapText.value = 0
    this.bitmapText.text = 0
    this.center()
  }

  checkTexture(textureName) {
    const tex = game.getTextureFromAtlases(textureName)
    if (tex === null || undefined) {
      return game.getTextureFromAtlases('usd')
    }
    return tex
  }

  roll(value, time) {
    const ease = 'expoScale(0.5,7,none)'
    const bitmapTween = gsap.to(this.bitmapText, time, {
      value,
      ease,
      onUpdate: () => {
        this.onUpdate()
      },
      onComplete: () => {
        bitmapTween.kill()
        const jumpTween = gsap.to(this.baseValue, {
          y: this.baseValue.y - 10,
          duration: 0.2,
          ease: ease,
          onComplete: () => {
            jumpTween.kill()
            const jumpTweenBack = gsap.to(this.baseValue, {
              y: this.baseValue.y + 10,
              duration: 0.2,
              ease: ease,
              onComplete: () => {
                jumpTweenBack.kill()
              },
            })
          },
        })
      },
    })
  }

  resizeText() {
    if (this.maxWidth > 0) {
      if (this.bitmapText.width > this.debug.width) {
        this.bitmapText.fontSize--
        setTimeout(() => {
          this.resizeText()
        }, 30)
      }
    }

  }

  update(value) {
    this.bitmapText.text = value
    if (this.bitmapText.width > this.debug.width) {
      this.resizeText()
    }

    if (this.hasCurrency && (this.bitmapText.prevLength + 2) < this.bitmapText._textWidth || (this.bitmapText.prevLength - 2) > this.bitmapText._textWidth) {
      const xOffset = this.bitmapText._textWidth * this._currencyProps.offsetX
      this.bitmapText.prevLength = this.bitmapText.text.length
      const ease = 'slow.out'
      const bitmapTween = gsap.to(this.currencyImage, {
        x: xOffset,
        duration: 0.2,
        ease: ease,
        onComplete: () => {
          bitmapTween.kill()
        },
      })
    }

  }

  onUpdate() {
    this.update(FormatService.currency(this.bitmapText.value, false))
  }

  createSignals(data) {

    if (data.show) {
      signal.on(data.show, this.show.bind(this))
    }

    if (data.hide) {
      signal.on(data.hide, this.hide.bind(this))
    }

    if (data.jump) {
      signal.on(data.jump, this.jump.bind(this))
    }

    if (data.win) {
      signal.on(data.win, this.roll.bind(this))
    }

  }

  createBaseValue() {
    return new PIXI.Container()
  }

  jump() {
    const ease = 'expoScale.out'
    const jumpTween = gsap.to(this.bitmapText, {
      y: this.bitmapText.y - 10,
      duration: 0.2,
      ease: ease,
      onComplete: () => {
        jumpTween.kill()
        const jumpTweenBack = gsap.to(this.bitmapText, {
          y: this.bitmapText.y + 10,
          duration: 0.2,
          ease: ease,
          onComplete: () => {
            jumpTweenBack.kill()
          },
        })
      },
    })
  }

  show() {
    this.visible = true
  }

  hide() {
    this.visible = false
  }

  createBitmap(container, data) {
    this.bitmapText  = new PIXI.BitmapText(data.text, data.style)

    this.bitmapText.fontSize = this._data.fontSize || 350
    this.startFontSize = data.style.fontSize
    this.bitmapText.value = 0
    this.bitmapText.anchor.set(data.anchorX ?? 0.5, data.anchorY || 1)
    this.bitmapText.text = 100000
    this.bitmapText.prevLength = 0
    this.text =  this.bitmapText
    container.addChild(this.bitmapText)

  }

  createCurrency(data) {
    this._currency = game.models.GameModel.currency

    const curTexture = this.checkTexture(this._currency.toLowerCase())
    this._currencyProps = {}
    this._currencyProps.scale = data.curScale || 0.35
    this._currencyProps.anchorX = data.curAnchorX || 0
    this._currencyProps.anchorY = data.curAnchorY || 0
    this._currencyProps.offsetX = data.curOffsetX ?? 10
    this._currencyProps.offsetY = data.curOffsetY || -45

    const currencyImage = new PIXI.Sprite(curTexture)
    currencyImage.x = this._currencyProps.offsetX
    currencyImage.y = this._currencyProps.offsetY
    currencyImage.anchor.x = this._currencyProps.anchorX
    currencyImage.anchor.y = this._currencyProps.anchorY
    currencyImage.scale.set(this._currencyProps.scale)
    this.bitmapText.addChild(currencyImage)

    this.currencyImage = currencyImage
  }
}
